// frontend/src/store/dashboardStore.js
// Store de Zustand para gestionar los datos y el estado del Dashboard General.

import { create } from 'zustand';
import { getDashboardSummary as apiGetDashboardSummary, getMonthlyRevenueData as apiGetMonthlyRevenueData } from '../api/dashboard';
import { useAuthStore } from './authStore';
import { useUIStore } from './uiStore';

/**
 * @typedef {object} DashboardState
 * @property {object} summaryData - Objeto con las métricas resumen del dashboard (totales, por fase).
 * @property {Array<object>} monthlyRevenueData - Datos de ingresos por mes para gráficos históricos.
 * @property {boolean} isLoadingDashboard - Indica si se están cargando los datos del dashboard.
 * @property {boolean} isLoadingMonthlyRevenue - Indica si se están cargando los datos de ingresos mensuales.
 * @property {function(object)} fetchDashboardSummary - Función para cargar el resumen del dashboard.
 * @property {function(object)} fetchMonthlyRevenueData - Función para cargar los datos de ingresos mensuales.
 */

export const useDashboardStore = create((set) => ({
  summaryData: {
    totalExpectedAmount: 0,
    totalRealizedAmount: 0,
    activeProjectsCount: 0,
    phaseExpectedAmounts: { OLA: 0, MRA: 0, MPA: 0 },
    phaseRealizedAmounts: { OLA: 0, MRA: 0, MPA: 0 },
  },
  monthlyRevenueData: [],
  isLoadingDashboard: false,
  isLoadingMonthlyRevenue: false,

  /**
   * @function fetchDashboardSummary
   * @description Carga el resumen de métricas para el dashboard desde el backend.
   * @param {object} [filters={}] - Filtros opcionales (startDate, endDate, etc.).
   */
  fetchDashboardSummary: async (filters = {}) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para cargar el dashboard.', 'error');
      return;
    }

    set({ isLoadingDashboard: true });
    try {
      const data = await apiGetDashboardSummary(token, filters);
      set({ summaryData: data });
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar los datos del dashboard.', 'error');
      set({
        summaryData: {
          totalExpectedAmount: 0,
          totalRealizedAmount: 0,
          activeProjectsCount: 0,
          phaseExpectedAmounts: { OLA: 0, MRA: 0, MPA: 0 },
          phaseRealizedAmounts: { OLA: 0, MRA: 0, MPA: 0 },
        },
      });
    } finally {
      set({ isLoadingDashboard: false });
    }
  },

  /**
   * @function fetchMonthlyRevenueData
   * @description Carga los datos de ingresos mensuales desglosados desde el backend.
   * @param {object} [filters={}] - El objeto de filtros completo (period, initiativeTypeId).
   */
  fetchMonthlyRevenueData: async (filters = {}) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      useUIStore.getState().showTemporaryMessage('No autenticado para cargar ingresos mensuales.', 'error');
      return;
    }

    set({ isLoadingMonthlyRevenue: true });
    try {
      // CORRECCIÓN: Pasar el objeto de filtros completo a la API.
      const data = await apiGetMonthlyRevenueData(token, filters);
      set({ monthlyRevenueData: data });
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar ingresos mensuales.', 'error');
      set({ monthlyRevenueData: [] });
    } finally {
      set({ isLoadingMonthlyRevenue: false });
    }
  },
}));
